import { motion } from "framer-motion";
import { Calendar, MapPin, Users, ArrowRight, UserRound, Sparkles, MessageCircle, Clock } from "lucide-react";
import ImageCarousel from "../components/ImageCarousel";
import ContactForm from "../components/ContactForm";

const green = {
  brand: "#2D6A4F",
  deep: "#0D3A4F",
  light: "#E8F4EC",
  mist: "#F0FAF4",
  border: "#C5DFD0",
};

const encuentroInstalacionesPhotos = [
  "IMG_9331.jpeg",
  "IMG_9332.jpeg",
  "IMG_9343.jpeg",
  "IMG_9344.jpeg",
  "IMG_9345.jpeg",
  "IMG_9349.jpeg",
  "IMG_9351.jpeg",
  "IMG_9355.jpeg",
  "IMG_9356.jpeg",
  "IMG_9360.jpeg",
  "IMG_9361.jpeg",
  "IMG_9364.jpeg",
  "IMG_9365.jpeg",
  "IMG_9367.jpeg",
  "IMG_9368.jpeg",
  "IMG_9371.jpeg",
  "IMG_9377.jpeg",
  "IMG_9385.jpeg",
  "IMG_9386.jpeg",
  "IMG_9388.jpeg",
  "IMG_9389.jpeg",
  "IMG_9392.jpeg",
  "IMG_9396.jpeg",
  "IMG_9398.jpeg",
  "IMG_9400.jpeg",
  "IMG_9401.jpeg",
  "IMG_9403.jpeg",
  "IMG_9404.jpeg",
  "IMG_9406.jpeg",
].map((filename, index) => ({
  url: `/images/encuentro/instalaciones/${filename}`,
  caption: `Instalaciones del encuentro ${index + 1}`,
}));

const edicionesAnterioresPhotos = [
  { url: "/images/encuentro/adultos-ediciones/convivencia-mesa.jpg", caption: "Convivencia y conversación" },
  { url: "/images/encuentro/adultos-ediciones/dinamica-interior.jpg", caption: "Dinámica interior" },
  { url: "/images/encuentro/adultos-ediciones/actividad-mesa.jpg", caption: "Actividad en grupo" },
  { url: "/images/encuentro/adultos-ediciones/actividad-exterior.jpg", caption: "Dinámica al aire libre" },
  { url: "/images/encuentro/adultos-ediciones/conversacion-pergola.jpg", caption: "Conversaciones compartidas" },
  { url: "/images/encuentro/adultos-ediciones/trabajo-terraza.jpg", caption: "Trabajo en la terraza" },
  { url: "/images/encuentro/adultos-ediciones/detalle-reto.jpg", caption: "Detalle de una actividad" },
];

const experiencias = [
  {
    title: "Conversaciones sin techo",
    desc: "Debates enriquecedores en torno a temas que en la vida cotidiana raramente encuentran espacio.",
  },
  {
    title: "Dinámica libre",
    desc: "Habrá diferentes actividades a lo largo del fin de semana, pero también margen para elegir ritmo, descansar o seguir conversando.",
  },
  {
    title: "Tiempo libre de calidad",
    desc: "Momentos para disfrutar del entorno de la Sierra de Gredos, pasear, contemplar y compartir sin prisa.",
  },
  {
    title: "Comunidad que continúa",
    desc: "Las conexiones del encuentro no terminan el domingo. DAC-PAC acompaña la red que se crea.",
  },
];

const requisitos = [
  {
    icon: UserRound,
    title: "Mayores de 18 años",
    desc: "El encuentro está pensado para personas adultas.",
  },
  {
    icon: Users,
    title: "20 plazas disponibles",
    desc: "La próxima edición mantiene un grupo reducido para cuidar la experiencia.",
  },
  {
    icon: Sparkles,
    title: "Si te sientes identificado, puedes venir",
    desc: "No necesitas acreditar una evaluación. Puedes participar si te sientes identificado con la alta capacidad y con el tipo de encuentro propuesto.",
  },
];

export default function EncuentroAdultos() {
  return (
    <main data-testid="encuentro-adultos-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="relative px-5 pb-20 pt-24 lg:px-8" style={{ background: green.light }}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute -bottom-20 -right-20 opacity-[0.07]" width="500" height="500" viewBox="0 0 200 200">
            <path d="M100,5 Q195,100 100,195 Q5,100 100,5Z" fill={green.brand} />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="/images/logos/encuentro-dacpac.png"
                  alt="DAC-PAC Adultos"
                  className="h-10 w-10 object-contain"
                />
                <div className="section-label" style={{ color: green.brand }}>Encuentro DAC-PAC Adultos</div>
              </div>
              <h1
                style={{ fontFamily: "Playfair Display, serif", color: green.deep, lineHeight: 1.15 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                La primera edición ya abrió
                <br />
                <em style={{ color: green.brand }}>un espacio propio.</em>
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                El encuentro de mayo de 2026 ya se realizó. La próxima edición está prevista para septiembre de 2026, con 20 plazas disponibles para adultos con alta capacidad que buscan comunidad, conversaciones profundas y un entorno cuidado.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#C5DFD0]" style={{ color: green.deep }}>
                  <Calendar size={14} style={{ color: green.brand }} /> Próxima edición: septiembre 2026
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#C5DFD0]" style={{ color: green.deep }}>
                  <MapPin size={14} style={{ color: green.brand }} /> La Lobera de Gredos, Arenas de San Pedro
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-[#C5DFD0]" style={{ color: green.deep }}>
                  <Users size={14} style={{ color: green.brand }} /> Mayores de 18 años · 20 plazas
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="#contacto-adultos" data-testid="adultos-contacto-cta" className="btn-primary" style={{ background: green.brand }}>
                  Solicitar información <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-3xl overflow-hidden shadow-xl"
              style={{ height: "440px" }}
            >
              <img
                src="/images/encuentro/adultos-ediciones/dinamica-interior.jpg"
                alt="Dinámica de grupo durante una edición anterior del encuentro"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="section-label mb-5" style={{ color: green.brand }}>Qué es el encuentro</div>
              <h2
                style={{ fontFamily: "Playfair Display, serif", color: green.deep }}
                className="text-3xl md:text-4xl font-semibold mb-6"
              >
                Un entorno natural para
                <br />
                conectar y profundizar
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  El Encuentro DAC-PAC Adultos es un fin de semana diseñado para personas adultas que se reconocen con alta capacidad y que buscan algo que rara vez encuentran en su día a día.
                </p>
                <p>
                  Una comunidad de iguales donde las conversaciones van en serio. Un entorno natural que invita a la reflexión. Un espacio donde la intensidad no es un defecto, es el punto de partida.
                </p>
                <p>
                  La primera edición se celebró en mayo de 2026 en La Lobera de Gredos, Arenas de San Pedro. La próxima edición será en septiembre de 2026 y mantendrá el mismo cuidado por el grupo, los tiempos y la convivencia.
                </p>
                <p>
                  La dinámica es flexible: hay actividades y propuestas durante el fin de semana, pero también tiempo para descansar, pasear y disfrutar del entorno enriquecedor de la Sierra de Gredos.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden" style={{ height: "240px" }}>
                <img src="/images/encuentro/adultos-ediciones/actividad-mesa.jpg" alt="Actividad de grupo durante una edición anterior" className="w-full h-full object-cover" />
              </div>
              <div
                className="rounded-[24px] p-7 text-white shadow-[0_18px_40px_rgba(13,58,79,0.18)]"
                style={{ background: `linear-gradient(135deg, ${green.deep} 0%, ${green.brand} 100%)` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Clock size={18} className="text-[#DDF3E3]" />
                  <span style={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }} className="text-xl">
                    Próxima edición en septiembre de 2026
                  </span>
                </div>
                <p className="text-sm leading-6 text-[#E8F4EC]">
                  Habrá 20 plazas disponibles. Si quieres recibir la información cuando abramos detalles de fechas y organización, puedes escribirnos a través del formulario de contacto.
                </p>
              </div>
              <div
                className="rounded-[24px] p-7 border shadow-[0_18px_40px_rgba(45,106,79,0.08)]"
                style={{ background: "#F8FCF9", borderColor: green.border }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <MessageCircle size={18} style={{ color: green.brand }} />
                  <span style={{ fontFamily: "Playfair Display, serif", color: green.deep, fontWeight: 600 }} className="text-xl">
                    Información bajo solicitud
                  </span>
                </div>
                <p className="text-sm leading-6 text-slate-600">
                  El formulario de inscripción de la edición anterior queda deshabilitado. Para resolver dudas o recibir aviso de la próxima edición, utiliza el formulario de contacto de esta página.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="section-label mb-4" style={{ color: green.brand }}>Requisitos de participación</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-3xl md:text-4xl font-semibold">
              Lo esencial para venir
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {requisitos.map((item, i) => (
              <div
                key={i}
                className="rounded-[24px] border border-[#CFE2D5] bg-[#F8FCF9] p-6 shadow-[0_14px_34px_rgba(45,106,79,0.05)]"
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: green.light }}
                >
                  <item.icon size={22} style={{ color: green.brand }} />
                </div>
                <h3 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-xl font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-sm leading-7 text-slate-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: green.mist }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="section-label mb-4" style={{ color: green.brand }}>La experiencia</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-3xl md:text-4xl font-semibold">
              Qué vivirás en DAC-PAC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {experiencias.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`adultos-experience-${i}`}
                className="p-7 rounded-2xl bg-white border border-[#C5DFD0]"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-4 text-white text-sm font-bold"
                  style={{ background: green.brand }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-xl font-semibold mb-3">{item.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: green.brand }}>Ediciones anteriores</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-3xl md:text-4xl font-semibold">
              La primera edición, en imágenes
            </h2>
            <p className="text-slate-500 mt-4 text-sm max-w-xl mx-auto leading-relaxed">
              Una selección de la edición de mayo de 2026: dinámicas, conversaciones, trabajo compartido y momentos de convivencia en La Lobera de Gredos.
            </p>
          </div>
          <ImageCarousel images={edicionesAnterioresPhotos} height="430px" />
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: green.mist }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="section-label mb-4" style={{ color: green.brand }}>El lugar</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-3xl md:text-4xl font-semibold">
              La Lobera de Gredos
            </h2>
            <p className="text-slate-500 mt-4 text-sm max-w-xl mx-auto leading-relaxed">
              Un espacio privilegiado en Arenas de San Pedro, en plena Sierra de Gredos, diseñado para la desconexión y el encuentro auténtico.
            </p>
          </div>
          <ImageCarousel images={encuentroInstalacionesPhotos} height="420px" />
        </div>
      </section>

      <section id="contacto-adultos" className="py-24 px-5 lg:px-8" style={{ background: green.light }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <div className="section-label mb-5" style={{ color: green.brand }}>Contacto</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: green.deep }} className="text-3xl md:text-4xl font-semibold mb-5">
                Solicita información sobre la próxima edición
              </h2>
              <p className="text-slate-600 leading-relaxed">
                El formulario de inscripción anterior ya no está activo. Si quieres recibir información sobre la edición de septiembre de 2026 o plantearnos alguna duda, escríbenos desde aquí.
              </p>
            </div>
            <div className="lg:col-span-3 rounded-[24px] bg-white border p-6 md:p-8" style={{ borderColor: green.border }}>
              <ContactForm
                accentColor={green.brand}
                successBg="#F0FAF4"
                successBorder={green.border}
                origen="encuentro-adultos-septiembre-2026"
                initialMensaje="Me interesa recibir información sobre la próxima edición del Encuentro DAC-PAC Adultos en septiembre de 2026."
                submitLabel="Solicitar información"
                successText="Hemos recibido tu consulta. Te responderemos con la información disponible sobre la próxima edición."
                testPrefix="adultos-contacto"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
